import ProgressPerformance from "@Containers/mobile/progress/performance";
import ProgressSnapshot from "@Containers/mobile/progress/snapshot";
import { getLatestDefinedScore } from "@Containers/shared/progress/utils";
import MetricEventTypes from "@Enums/MetricEventTypes";
import MetricFilterTypes from "@Enums/MetricFilterTypes";
import type { MetricScores } from "@Helpers/types";
import { UseCommonProgressDataReturn } from "@hooks/game/progress/use-common-progress-data";
import useI18n from "@i18n";
import React, { useMemo, useState } from "react";

export interface UseMobileProgressDataReturn {
  performanceScores: MetricScores | null;
  snapshotStats: {
    activityMinutes: number;
    numberOfSessions: number;
    weeklyMinutes: number;
    targetTrainingTime: number;
  };
  areSnapshotStatsLoading: boolean;
  tabsConfig: any[];
  tabValue: number;
  setTabValue: (value: number) => void;
  tabsRef: React.RefObject<HTMLDivElement>;
}

export const useMobileProgressData = (commonData: UseCommonProgressDataReturn): UseMobileProgressDataReturn => {
  const { i18n } = useI18n();
  const {
    totalTrainingTime,
    totalSessions,
    weeklyTrainingTime,
    requiredTrainingTime,
    isTotalTimeLoading,
    isTotalSessionsLoading,
    isWeekTimeLoading,
    isCurrentBybIqLoading,
    isLongTermResultsLoading,
    longTermMetricResults,
  } = commonData;

  const [tabValue, setTabValue] = useState<number>(+(localStorage.getItem("currentProgressTabIndex") || 0));
  const tabsRef = React.useRef<HTMLDivElement>(null);

  const snapshotStats = useMemo(
    () => ({
      activityMinutes: totalTrainingTime / 60,
      numberOfSessions: totalSessions,
      weeklyMinutes: weeklyTrainingTime / 60,
      targetTrainingTime: requiredTrainingTime,
    }),
    [totalTrainingTime, totalSessions, weeklyTrainingTime, requiredTrainingTime]
  );

  const areSnapshotStatsLoading = isTotalTimeLoading || isTotalSessionsLoading || isWeekTimeLoading || isCurrentBybIqLoading;

  const performanceScores = useMemo<MetricScores | null>(() => {
    if (isLongTermResultsLoading || !longTermMetricResults) {
      return null;
    }

    return {
      [MetricFilterTypes.DECISION]: { score: getLatestDefinedScore(longTermMetricResults[MetricEventTypes.DECISION]) ?? 0, target: 100 },
      [MetricFilterTypes.SCAN_FREQUENCY]: { score: getLatestDefinedScore(longTermMetricResults[MetricEventTypes.SCAN_FREQUENCY]) ?? 0, target: 1 },
      [MetricFilterTypes.SCAN_TIMING]: { score: getLatestDefinedScore(longTermMetricResults[MetricEventTypes.SCAN_TIMING]) ?? 0, target: 100 },
      [MetricFilterTypes.CRITICAL_SCAN]: { score: getLatestDefinedScore(longTermMetricResults[MetricEventTypes.CRITICAL_SCAN]) ?? 0, target: 1 },
      [MetricFilterTypes.AWARENESS]: { score: getLatestDefinedScore(longTermMetricResults[MetricEventTypes.AWARENESS]) ?? 0, target: 100 },
    };
  }, [isLongTermResultsLoading, longTermMetricResults]);

  const getTabsConfig = () => {
    return [
      {
        id: "SNAPSHOT",
        label: i18n("progress.snapshot.title"),
        component: <ProgressSnapshot />,
      },
      {
        id: "PERFORMANCE",
        label: i18n("progress.performance.title"),
        component: <ProgressPerformance />,
      },
    ];
  };

  const tabsConfig = getTabsConfig();

  const handleTabChange = (newValue: number) => {
    setTabValue(newValue);
    localStorage.setItem("currentProgressTabIndex", newValue.toString());
  };

  return {
    performanceScores,
    snapshotStats,
    areSnapshotStatsLoading,
    tabsConfig,
    tabValue,
    setTabValue: handleTabChange,
    tabsRef,
  };
};
